import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import moment from "moment";

import { makeStyles } from "@material-ui/core/styles";
import { useContext } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import CircularProgress from "@material-ui/core/CircularProgress";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  timeRow: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
  hint: {
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
}));

const ScheduleBlockModal = ({ open, onClose, onSaved }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const isAdmin = user.profile === "admin";

  const [userId, setUserId] = useState("");
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [isFullDay, setIsFullDay] = useState(true);
  const [startTime, setStartTime] = useState("08:00");
  const [endTime, setEndTime] = useState("09:00");
  const [reason, setReason] = useState("");
  const [providers, setProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    const fetchProviders = async () => {
      setLoadingProviders(true);
      try {
        const { data } = await api.get("/users/list");
        setProviders((data || []).filter((u) => u.isServiceProvider));
      } catch (err) {
        toastError(err);
      } finally {
        setLoadingProviders(false);
      }
    };
    fetchProviders();
  }, [open]);

  const handleClose = () => {
    setUserId("");
    setDate(moment().format("YYYY-MM-DD"));
    setIsFullDay(true);
    setStartTime("08:00");
    setEndTime("09:00");
    setReason("");
    onClose();
  };

  const handleSubmit = async () => {
    if (isAdmin && !userId) {
      toast.error("Selecione o profissional.");
      return;
    }
    if (!date) {
      toast.error("Informe a data do bloqueio.");
      return;
    }
    if (!isFullDay && (!startTime || !endTime || startTime >= endTime)) {
      toast.error("O horário de início deve ser menor que o de término.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/schedule-blocks", {
        userId: isAdmin ? userId : undefined, // backend usa req.user.id para não-admin
        date,
        isFullDay,
        startTime: isFullDay ? undefined : startTime,
        endTime: isFullDay ? undefined : endTime,
        reason: reason.trim() || undefined,
      });
      toast.success("Bloqueio adicionado com sucesso.");
      onSaved();
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth scroll="paper">
      <DialogTitle>Adicionar Bloqueio de Agenda</DialogTitle>

      <DialogContent dividers>
        {isAdmin && (
          <FormControl variant="outlined" margin="dense" fullWidth>
            <InputLabel>Profissional</InputLabel>
            <Select
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              label="Profissional"
              disabled={loadingProviders}
            >
              {providers.length === 0 && !loadingProviders && (
                <MenuItem value="" disabled>
                  Nenhum profissional cadastrado
                </MenuItem>
              )}
              {providers.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <TextField
          label="Data"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          margin="dense"
          fullWidth
          inputProps={{ min: moment().format("YYYY-MM-DD") }}
        />

        <FormControlLabel
          style={{ marginTop: 8, marginBottom: 4 }}
          control={
            <Switch
              checked={isFullDay}
              onChange={(e) => setIsFullDay(e.target.checked)}
              color="primary"
            />
          }
          label="Bloquear o dia inteiro"
        />

        {!isFullDay && (
          <Box className={classes.timeRow}>
            <TextField
              label="Início"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
              variant="outlined"
              margin="dense"
              style={{ flex: 1 }}
            />
            <TextField
              label="Fim"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
              variant="outlined"
              margin="dense"
              style={{ flex: 1 }}
            />
          </Box>
        )}

        <TextField
          label="Motivo (opcional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          variant="outlined"
          margin="dense"
          fullWidth
          placeholder="ex: Férias, Consulta médica, Feriado..."
        />

        <Typography variant="caption" className={classes.hint}>
          Os horários bloqueados não aparecerão para agendamento na página pública.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={submitting || loadingProviders}
        >
          {submitting ? <CircularProgress size={20} color="inherit" /> : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleBlockModal;
